<template>
  <div class="manufacturer-content">
    <div class="manufacturer-details-container">
      <div class="manufacturer-details-container-header">
        {{ titleText }}
        <AuditInformation 
          v-if="!loading && manufacturer && manufacturer.id > 0 && manufacturer.createdByUser && manufacturer.createdDate && manufacturer.lastModifiedByUser && manufacturer.lastModifiedDate"
          :created-by-user="manufacturer.createdByUser"
          :created-date="manufacturer.createdDate"
          :last-modified-by-user="manufacturer.lastModifiedByUser"
          :last-modified-date="manufacturer.lastModifiedDate"
        />
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading && manufacturer"
        class="manufacturer-details-container-content"
      >
        <LockingButton
          class="save-button"
          :disabled="!isDirty"
          :locked="saving"
          :text="'Save'"
          @click="onSaveManufacturerClicked"
        />
        <div class="manufacturer-details-flex-container-side-by-side">
          <TextInput
            v-model="manufacturer.name"
            :show-required="true"
            label="Name"
            class="first-item"
          />
          <PhoneNumberInput
            v-model="manufacturer.phoneNumber"
            class="second-item"
          />
        </div>
        <div class="manufacturer-details-flex-container-side-by-side">
          <TextInput
            v-model="manufacturer.email"
            label="Email"
            class="first-item"
          />
          <TextInput
            v-model="manufacturer.website"
            label="Website"
            class="second-item"
          />
        </div>
        <div class="manufacturer-details-flex-container-side-by-side">
          <TextInput
            v-model="manufacturer.addressLine1"
            label="Address Line 1"
            class="first-item"
          />
          <TextInput
            v-model="manufacturer.addressLine2"
            label="Address Line 2"
            class="second-item"
          />
        </div>
        <div class="manufacturer-details-flex-container-side-by-side">
          <TextInput
            v-model="manufacturer.city"
            label="City"
            class="first-item"
          />
          <div class="second-item state-and-postal-code">
            <Dropdown
              v-model="manufacturer.stateId"
              label="State"
              :options="stateDropdownOptions"
              class="first-item"
            />
            <TextInput
              v-model="manufacturer.postalCode"
              label="Postal Code"
              class="second-item"
            />
          </div>
        </div>
      </div>
    </div>
    <div class="contacts-container">
      <div class="contacts-container-header">
        <div class="contacts-container-header-text">
          Contacts
        </div>
        <a 
          v-show="!loading && manufacturer && manufacturer.id > 0"
          class="create-contact"
          @click="onCreateContactClicked"
        >
          Create
        </a>
      </div>
      <LoadingPulse
        :loading="loading"
        class="loading-spinner"
      />
      <div
        v-if="!loading && manufacturer"
      >
        <ContactTile
          v-for="contact in manufacturer.contacts"
          :key="contact.id"
          :contact="contact"
          @delete="onDeleteContact"
          @edit="onEditContact"
        />
      </div>
      <div
        v-if="(!manufacturer || manufacturer.id === 0) && !loading"
        class="message"
      >
        * Contacts can be added to the manufacturer once it is initally created.
      </div>
    </div>
    <transition name="slide-up">
      <ContactModal 
        v-if="showContactModal && contactToEdit"
        :contact="contactToEdit"
        :manufacturer-id="id"
        @contact-changed="getManufacturer"
        @close="showContactModal = false"
      />
    </transition>
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from 'vue'
import TextInput from '@/components/shared/TextInput.vue'
import LoadingPulse from '@/components/shared/LoadingPulse.vue'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import { ManufacturerService } from '@/services/manufacturer.service'
import { Contact } from '@/@types/responses/contact/Contact'
import ContactModal from '@/components/modals/ContactModal.vue'
import Toaster from '@meforma/vue-toaster'
import { ManufacturerDetails } from '@/@types/responses/manufacturer/ManufacturerDetails'
import ContactTile from '@/components/elements/ContactTile.vue'
import { ContactService } from '@/services/contact.service'
import AuditInformation from '@/components/elements/AuditInformation.vue'
import PhoneNumberInput from '@/components/shared/PhoneNumberInput.vue'
import { ManufacturerMessage } from '@/@types/messages/ManufacturerMessage'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'
import Dropdown from '@/components/shared/Dropdown.vue'
import { DropdownOptionResponse } from '@/@types/responses/common/DropdownOptionResponse'
import { StateService } from '@/services/states.service'

export default defineComponent({
  name: 'Manufacturer',
  components: {
    AuditInformation,
    ContactModal,
    ContactTile,
    Dropdown,
    LoadingPulse,
    LockingButton,
    PhoneNumberInput,
    TextInput
  },
  beforeRouteLeave() {
    if (this.showContactModal) {
      this.showContactModal = false
      return false
    }

    return true
  },
  props: {
    id: {
      type: Number,
      required: true
    }
  },
  setup() {
    const contactService = inject(PROVIDER_CONSTANTS.contactService) as ContactService
    const manufacturerService = inject(PROVIDER_CONSTANTS.manufacturerService) as ManufacturerService
    const stateService = inject(PROVIDER_CONSTANTS.stateService) as StateService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

   return {
      contactService,
      manufacturerService,
      stateService,
      toast
    }
  },
  data() {
    let contactToEdit: Contact|undefined
    let manufacturer: ManufacturerDetails|undefined
    let unchangedManufacturer: ManufacturerDetails|undefined

    return {
      loading: true,
      manufacturer: manufacturer,
      unchangedManufacturer: unchangedManufacturer, 
      saving: false,
      showContactModal: false,
      stateDropdownOptions: [] as DropdownOptionResponse<number>[],
      contactToEdit
    }
  },
  computed: {
    isDirty(): boolean {
      const a = this.manufacturer
      const b = this.unchangedManufacturer

      return (a?.name !== b?.name ||
             a?.website !== b?.website ||
             a?.email !== b?.email ||
             a?.phoneNumber !== b?.phoneNumber ||
             a?.addressLine1 !== b?.addressLine1 ||
             a?.addressLine2 !== b?.addressLine2 ||
             a?.city !== b?.city ||
             a?.stateId !== b?.stateId ||
             a?.postalCode !== b?.postalCode) && a?.name.trim() !== ''
    },
    titleText(): string {
      return !this.manufacturer || this.manufacturer.id === 0 ? 'Create Manufacturer' : 'Edit Manufacturer'
    }
  },
  watch: {
    '$route': {
      handler: function(): void {
        this.updatePageTitle()
      }
    }
  },
  mounted() {
    this.getStates().then(() => {
      if (this.id > 0) {
        this.getManufacturer()
      } else {
        this.manufacturer = new ManufacturerDetails(0)
        this.unchangedManufacturer = new ManufacturerDetails(0)
        this.loading = false
      }
    })
  },
  methods: {
    getManufacturer(): Promise<void> {
      return this.manufacturerService.getManufacturer(this.manufacturer ? this.manufacturer.id : this.id).then(manufacturer => {
        this.manufacturer = manufacturer
        this.unchangedManufacturer = JSON.parse(JSON.stringify(manufacturer))
        this.updatePageTitle()
        this.loading = false
      }).catch(error => {
        this.saving = false

        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the manufacturer. Try reloading the page.')
        }
      })
    },
    getStates(): Promise<void> {
      return this.stateService.getStateDropdownOptions().then(dropdownOptionsResponse => {
        this.stateDropdownOptions = dropdownOptionsResponse.dropdownOptions
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to load the states. Try reloading the page.')
        }
      })
    },
    onCreateContactClicked(): void {
      this.contactToEdit = new Contact(0)
      this.showContactModal = true
    },
    onDeleteContact(contact: Contact): void {
      this.contactService.deleteContact(contact.id).then(() => {
        this.getManufacturer().then(() => {
          this.toast.success('The contact was deleted successfully.')
        })
      }).catch(error => {
        if (error instanceof ErrorsResponse) {
          const errorsResponse = error as ErrorsResponse

          if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

          this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
        } else {
          this.toast.error('Failed to delete the contact. Please try again.')
        }
      })
    },
    onEditContact(contact: Contact): void {
      this.contactToEdit = contact
      this.showContactModal = true
    },
    onSaveManufacturerClicked(): void {
      if (!this.manufacturer) return

      this.saving = true
      
      const manufacturerMessage = new ManufacturerMessage(this.manufacturer.name, this.manufacturer.website, this.manufacturer.email, this.manufacturer.phoneNumber, this.manufacturer.addressLine1, this.manufacturer.addressLine2, this.manufacturer.city, this.manufacturer.stateId, this.manufacturer.postalCode)
      if (this.manufacturer.id === 0)
        this.manufacturerService.createManufacturer(manufacturerMessage).then(objectCreatedResponse => {
          if (!this.manufacturer) return

          this.manufacturer.id = objectCreatedResponse.id
          this.getManufacturer().then(() => {
            this.$router.replace(`/manufacturer/${objectCreatedResponse.id}`)
            this.saving = false
            this.toast.success('The manufacturer was created successfully.')
          })
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to create the manufacturer. Please try again.')
          }
        })
      else
        this.manufacturerService.updateManufacturer(this.id, manufacturerMessage).then(() => {
          this.toast.success('The manufacturer was updated successfully.')
          this.saving = false
          this.$router.push('/manufacturers')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to update the manufacturer. Please try again.')
          }
        })
    },
    updatePageTitle(): void {
      if (this.id > 0 && this.manufacturer)
        document.title = `${this.manufacturer.name} - Short Action Customs, LLC`
    }
  }
})
</script>

<style scoped lang="scss">
.manufacturer-content {
  display: flex;
  flex-direction: column;
  padding: 15px;

  @include desktop {
    flex-direction: row;
  }

  > .manufacturer-details-container {
    display: flex;
    flex-direction: column;
    margin-bottom: 15px;
    position: relative;

    @include desktop {
      flex: 1 1 70%;
      height: max-content;
      padding: 15px;
      background-color: $Color-fill-gray;
      border-radius: 2px;
      @include element-shadow;
    }

    > .manufacturer-details-container-header {
      line-height: 37px;
      font-size: 24px;
      font-weight: bold;
      padding-bottom: 10px;
      border-bottom: 1px solid $Color-border-gray;
    }

    > .loading-spinner {
      margin: auto;
      padding: 20px;
    }

    > .manufacturer-details-container-content {
      padding-top: 15px;

      > .save-button {
        position: absolute;
        top: 0;
        right: 0;

        @include desktop {
          top: 15px;
          right: 15px;
        }
      }

      > .manufacturer-details-flex-container-side-by-side {
        display: flex;
        flex-direction: column;

        @include desktop {
          flex-direction: row;
        }

        > .first-item {
          margin-bottom: 15px;
          flex: 1;

          @include desktop {
            flex: 0 0 calc(50% - 10px);
            margin-right: 10px;
          }
        }

        > .second-item {
          margin-bottom: 15px;
          flex: 1;

          @include desktop {
            flex: 0 0 50%;
          }

          &.state-and-postal-code {
            display: flex;

            > .first-item {
              flex: 0 0 calc(50% - 10px);
              margin-right: 10px;
            }

            > .second-item {
              flex: 0 0 50%;
            }
          }
        }
      }
    }
  }

  > .contacts-container {
    position: relative;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding-bottom: 15px;

    @include desktop {
      flex: 1 1 30%;
      padding-left: 20px;
    }

    > .contacts-container-header {
      display: flex;
      margin-bottom: 15px;

      > .contacts-container-header-text {
        margin: 0 auto 0 0;
        font-size: 24px;
        font-weight: bold;
      }

      > .create-contact {
        @include flat-button;
        margin: auto 0;
      }
    }

    > .loading-spinner {
      margin: auto;
    }

    > .message {
      font-style: italic;
      color: $Color-font-medium;
    }
  }

  > .slide-up {
    @include modal-animation;
  }
}
</style>
